"use client";

import React, { useMemo, useState } from "react";
import styles from "../css/TodaysAppointments.module.css";
import Button from "../../../../components/Button.jsx";
import { formatISOToDDMMYYYY } from "../utils/scheduleConsultUtils.js";

export default function TodaysAppointments({ appointments = [], onCancel, onSendReminder, onViewAll }) {
  const pageSize = 5;
  const [page, setPage] = useState(1);
  const [confirmId, setConfirmId] = useState(null);
  const formatTime = (t) => {
    if (!t) return "";
    const [h, m, s] = String(t).split(":");
    const hh = String(h ?? "00").padStart(2, "0");
    const mm = String(m ?? "00").padStart(2, "0");
    const ssRaw = String(s ?? "00");
    const ss = ssRaw.replace(/[^0-9].*$/, "").padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  // Filter appointments to show only pending and cancelled ones
  const todaysAppointments = useMemo(() => {
    return appointments.filter(a => {
      const status = (a.status || "Pending").toLowerCase();
      return status === "pending" || status === "cancelled";
    });
  }, [appointments]);

  const totalPages = Math.max(1, Math.ceil(todaysAppointments.length / pageSize));
  const visible = useMemo(() => {
    const start = (page - 1) * pageSize;
    return todaysAppointments.slice(start, start + pageSize);
  }, [todaysAppointments, page]);

  const goto = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3 className={styles.title}>Today's Appointments</h3>
        <Button className={styles.viewAllBtn} variant="primary" type="button" onClick={onViewAll}>View All Appointment</Button>
      </div>

      <div className={styles.list}>
        {visible.map((a) => (
          <div key={a.id} className={styles.card}>
            <div className={styles.info}>
              <div className={styles.name}>{a.patient?.name}</div>
              <div className={styles.meta}>{formatISOToDDMMYYYY(a.date) || a.date}{a.time ? ` - ${formatTime(a.time)}` : ""}</div>
            </div>
            <div className={styles.actions}>
              {(a.status || "Pending").toLowerCase() !== "cancelled" && (
                <button
                  title="Cancel appointment"
                  className={styles.iconDelete}
                  aria-label="Cancel appointment"
                  onClick={() => setConfirmId(a.id)}
                >
                  <svg className={styles.trashIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M19 6L18.1 20.2C18.04 21.17 17.23 22 16.26 22H7.74C6.77 22 5.96 21.17 5.9 20.2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M8 6V4.5C8 3.67 8.67 3 9.5 3H14.5C15.33 3 16 3.67 16 4.5V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
              <button title="Send Reminder" className={styles.iconSend} onClick={() => onSendReminder?.(a)}>
                <svg className={styles.planeIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <span className={`${styles.badge} ${(a.status || "Pending").toLowerCase() === "cancelled" ? styles.cancelledBadge : ""}`}>
                {a.status || "Pending"}
              </span>
            </div>
          </div>
        ))}
        {todaysAppointments.length === 0 && (
          <div className={styles.empty}>No appointments for today.</div>
        )}
      </div>

      {todaysAppointments.length > 0 && (
        <div className={styles.pagination}>
          <button onClick={() => goto(page - 1)} disabled={page === 1}>{"<"}</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button key={p} className={p === page ? styles.activePage : ""} onClick={() => goto(p)}>
              {p}
            </button>
          ))}
          <button onClick={() => goto(page + 1)} disabled={page === totalPages}>{">"}</button>
        </div>
      )}
      {confirmId && (
        <div className={styles.confirmOverlay} onClick={() => setConfirmId(null)}>
          <div className={styles.confirmContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>Cancel Appointment</h3>
            <p>Are you sure you want to cancel this appointment?</p>
            <div className={styles.confirmActions}>
              <Button variant="approve" type="button" onClick={() => setConfirmId(null)}>Cancel</Button>
              <Button variant="cancel" type="button" onClick={() => { onCancel?.(confirmId); setConfirmId(null); }}>Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
